import { FC, useState, MutableRefObject, ReactNode } from 'react';
import { Box, Divider, Stack } from '@mui/material';
import {
  gridPreferencePanelStateSelector,
  GridPreferencePanelsValue,
  GridViewColumnIcon,
} from '@mui/x-data-grid';
import { GridApiCommunity } from '@mui/x-data-grid/internals';
import TableRowsIcon from '@mui/icons-material/TableRows';
import SaveAltIcon from '@mui/icons-material/SaveAlt';

import { DataGridTool } from '../V1/DataGridTool';
import { DataGridSearch } from './DataGridSearch';

interface DataGridToolbarProps {
  apiRef: MutableRefObject<GridApiCommunity>;
  tools?: ReactNode;
  actions?: ReactNode;
  tooltipColumns?: string;
  tooltipDensity?: string;
  tooltipFilters?: string;
  tooltipExport?: string;
  tooltipSearch?: string;
  showAddButton?: boolean;
}

export const DataGridToolbar: FC<DataGridToolbarProps> = ({
  apiRef,
  tools,
  actions,
  tooltipColumns = 'Manage Columns',
  tooltipDensity = 'Row Size',
  tooltipFilters = 'Filter',
  tooltipExport = 'Export to CSV',
}) => {
  const [density, setDensity] = useState<'compact' | 'standard' | 'comfortable'>('standard');
  const [searchExpanded, setSearchExpanded] = useState(false);

  const togglePreferencePanel = (preference: string) => {
    const preferencePanel = gridPreferencePanelStateSelector(apiRef.current.state);
    if (preferencePanel.open) {
      apiRef.current.hidePreferences();
    } else {
      if (preference === 'columns') {
        apiRef.current.showPreferences(GridPreferencePanelsValue.columns);
      } else if (preference === 'filters') {
        apiRef.current.showFilterPanel();
      } else if (preference === 'density') {
        if (density === 'compact') {
          apiRef.current.setDensity('standard');
          setDensity('standard');
        } else if (density === 'standard') {
          apiRef.current.setDensity('comfortable');
          setDensity('comfortable');
        } else {
          apiRef.current.setDensity('compact');
          setDensity('compact');
        }
      } else if (preference === 'export') {
        apiRef.current.exportDataAsCsv();
      }
    }
  };

  const handleSearchToggle = (expanded: boolean) => {
    setSearchExpanded(expanded);
  };

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{
        px: 2,
        backgroundColor: 'background.paper',
        borderRadius: 3,
        p: 2,
        position: 'relative',
        overflowX: 'auto',
        '&::-webkit-scrollbar': {
          display: 'none',
        },
        msOverflowStyle: 'none',
        scrollbarWidth: 'none',
      }}
    >
      {!searchExpanded && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {tools}
          <DataGridTool
            onClick={() => togglePreferencePanel('columns')}
            tooltip={tooltipColumns}
            icon={<GridViewColumnIcon />}
            isIconButton
          />
          <DataGridTool
            onClick={() => togglePreferencePanel('density')}
            tooltip={tooltipDensity}
            icon={<TableRowsIcon />}
            isIconButton
          />
          {/* Removing for now */}
          {/* <DataGridTool
            onClick={() => togglePreferencePanel('filters')}
            tooltip={tooltipFilters}
            icon={<GridFilterListIcon />}
            isIconButton
          /> */}
          <DataGridTool
            onClick={() => togglePreferencePanel('export')}
            tooltip={tooltipExport}
            icon={<SaveAltIcon />}
            isIconButton
          />
          <Divider orientation="vertical" sx={{ height: 24, mx: 1 }} />
        </Box>
      )}
      {!searchExpanded && actions}
      <DataGridSearch 
        isExpanded={searchExpanded}
        onToggleExpanded={handleSearchToggle}
      />
    </Stack>
  );
};

export default DataGridToolbar;
